import React, { useState } from 'react'
import { getCDNImageUrl, getSeoShopUrl } from '@/lib/utilities/Utils'
import dynamic from 'next/dynamic'
import style from './index.module.scss'
import Link from 'next/link'
// import CustomImage from '../custom-image'
import Icons from '@/components/icons'
import Modal from '@/components/Modal'
import useIsMobile from '@/hooks/useIsMobile'
import StoreSocialShare from '@/routes/store/see-all/StoreSocialShare'
import reorderJson from '@/lottie/lottie-files/reorder.json'
import Image from 'next/image'
import StoreTourMedia from '@/components/common/ShopTourMedia'

const CustomLottiePlayer = dynamic(async () => await import('@/lottie/index'), {
  ssr: false,
  loading: () => <></>
})

interface ShopCardProps {
  onClick?: () => void
  item: {
    rating: number
    isPremium: boolean
    isGreatOffer: boolean
    isTrending: boolean
    isWideVariety: boolean
    isBestRated: boolean
    isNewlyOpened: boolean
    isRecommendedStore?: boolean
    isRepeatOrderStore?: boolean
    isLLDP?: boolean
    lldpDeliveryMsg?: string
    isFreeSample?: boolean
    offerTextV2?: string
    country?: string
    city?: string
    locality?: string
    storeType: {
      name?: string
      displayName?: string
    }
    name?: string
    displayName?: string
    storeId?: number
    code?: number
    logo?: string
    shopImagePlaceholder?: string
    productTransferMode: {
      homeDelivery: boolean
      storePickup: boolean
    }
    averageDeliveryTimeString: string
    calculatedDistanceMeasurement: string
    calculatedDistance: number
    offerText: string
    deliveryTimeMsg?: string

    shopMedia?: Array<{
      type: 'IMAGE' | 'VIDEO'
      id: number
      thumbnail?: string
      url: string
    }>
  }
  showStoreTour?: boolean
}

const ShopCard = (props: ShopCardProps): JSX.Element => {
  const {
    item,
    showStoreTour = false
  } = props
  const isMobile = useIsMobile()

  const rating = item?.rating
  const pickupOnlyAllowed = (!item?.productTransferMode?.homeDelivery && item?.productTransferMode?.storePickup)
  // const distanceString = item.calculatedDistanceMeasurement === 'meters'
  //   ? `${(item.calculatedDistance / 1000).toFixed(2)} Kms`
  //   : `${item.calculatedDistance}${item.calculatedDistanceMeasurement}`

  const badges = []
  if (item?.isFreeSample === true) badges.push('free-sample')
  if (item?.isPremium) badges.push('premium')
  if (item?.isGreatOffer) badges.push('shop-offer')
  if (item?.isTrending) badges.push('trending-icon')
  if (item?.isWideVariety) badges.push('wide-variety')
  if (item?.isBestRated) badges.push('best-rated')
  if (item?.isNewlyOpened) badges.push('newly-opened')

  const renderOfferText = (text: string | undefined): React.JSX.Element[] => {
    if (text == null) {
      return []
    }
    const parts = text?.split('|')
    return parts.map((part, index) => {
      if (index % 2 === 0) {
        return <span key={index}>{part}</span>
      }

      return (
        <span key={index} className="font-bold">
          {part}
        </span>
      )
    })
  }

  const { shopUrl } = getSeoShopUrl(
    item.country,
    item.city,
    item.locality,
    (item?.storeType?.name !== '' ? item?.storeType?.name : item?.storeType?.displayName),
    (item.name !== '' ? item.name : item.displayName),
    item.storeId ?? item.code ?? ''
  )
  const [shareModal, setShareModal] = useState(false)

  const deliveryMsg: string = ((item?.isLLDP) === true)
    ? (item?.lldpDeliveryMsg ?? '').replace(/\s*-\s*/, ' ')
    : (item?.deliveryTimeMsg ?? '')

  const byMatch = deliveryMsg.match(/^(.*)\sby\s(.*)$/i)
  const inMatch = deliveryMsg.match(/^(.*)\sin\s(.*)$/i)

  const matchedParts = byMatch ?? inMatch

  const prefix = matchedParts?.[1]
  const suffix = matchedParts?.[2]

  const keyword = (byMatch != null) ? 'by' : (inMatch != null) ? 'in' : ''
  // const { referralCode } = useSelector((state: RootState) => state?.referral)

  return (
    <div
      className={`${style.store_card_wrapper} relative top-[-2px] ${
        !isMobile && item?.isRepeatOrderStore === true
          ? 'rounded-bl-[32px] rounded-br-[32px] bg-[#DFF8EB]'
          : ''
      } rounded-[32px]`}
    >

      {item?.isRepeatOrderStore === true && (
        isMobile
          ? (
            <div className="flex mb-2 gap-1.5 items-center">
              <CustomLottiePlayer
                animationData={reorderJson}
                width={23}
                height={23}
              />
              <p className="text-black font-extra-bold text-[16px]">
                Previously Shopped
              </p>
            </div>
            )
          : (
            <div
              className="rounded-t-[32px] flex -mb-[2px] py-1 gap-1.5 items-center justify-center bg-[#DFF8EB] max-w-[370px]"
            >
              <CustomLottiePlayer
                animationData={reorderJson}
                width={16}
                height={16}
              />
              <p className="text-black font-extra-bold text-[12px]">
                Previously Shopped
              </p>
            </div>
            )
      )}

      {/* WHITE STORE CARD */}
      <div
        className={`${style.store_card_container} border mt-[0.2rem] bg-white`}
      >

        {/* SHOP LINK */}
        <Link
          href={shopUrl}
          onClick={() => {
            window.gtag?.('event', 'store_card_click', {
              store_name: item?.name ?? item?.displayName ?? '',
              store_id: item?.storeId ?? item?.code ?? ''
            })
          }}
        >

          <Modal
            show={shareModal}
            onHide={() => {
              setShareModal(false)
            }}
            bodyStyle={{
              top: isMobile ? '50%' : '5px',
              right: isMobile ? '50%' : '5px',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              transform: isMobile
                ? 'translate(50%,-50%)'
                : 'translate(0, 0)',
              width: isMobile ? '' : '450px'
            }}
            size="md"
          >
            <div className="w-full flex items-center justify-center">
              <StoreSocialShare shop={item}/>
            </div>
          </Modal>

          {/* OFFER */}
          {item?.offerTextV2 !== '' && (
            <div className="w-full bg-[linear-gradient(90deg,#FDE7F6_8%,#FFF6FD_10%,#FFFFFF_100%)] h-[24px] mb-[12px]">
              <div className="absolute left-[-6px] top-[10px] flex items-center">
                <Icons name="shopofferstrip"/>

                <p className="text-[12px] mb-[2px]">
                  {renderOfferText(item?.offerTextV2)}
                </p>
              </div>
            </div>
          )}

          {/* STORE DETAILS */}
          <div className="overflow-hidden relative bg-[#fff]">
            <div className="flex">

              {/* Logo */}
              <div className="w-[90px] h-[90px] rounded-[24px]">
                <Image
                  src={getCDNImageUrl(
                    item?.logo ?? item?.shopImagePlaceholder
                  )}
                  alt={
                    item?.name ??
                    item?.displayName ??
                    'Shop Image'
                  }
                  title={
                    item?.name !== ''
                      ? item?.name
                      : item?.displayName
                  }
                  width={90}
                  height={90}
                  className="w-full h-full object-cover rounded-[24px]"
                />
              </div>

              {/* Store details */}
              <div className="flex-1 pl-[12px] flex flex-col justify-center">

                {item?.isRecommendedStore === true && (
                  <p className="font-black text-[10px] text-[#B8238E] mb-[6px] leading-none">
                    RECOMMENDED
                  </p>
                )}

                <p
                  className={style.store_title}
                  title={
                    item?.name !== ''
                      ? item?.name
                      : item?.displayName
                  }
                >
                  {item?.name !== ''
                    ? item?.name
                    : item?.displayName}
                </p>

                <div className="flex items-center mt-[2.5px]">

                  {rating !== 0 && (
                    <div className="text-sm">
                      <div
                        className="flex min-w-[37px] justify-center items-center bg-[#B8238E] rounded-[18px] py-[2px] px-[4px]">
                        <Icons
                          name="shop-rating2"
                          height={12}
                          width={12}
                        />

                        <span className="text-white text-[10px] font-bold ml-[1px]">
                          {rating}
                        </span>
                      </div>
                    </div>
                  )}

                  {pickupOnlyAllowed
                    ? (
                      <span className="text-sm text-gray-600 font-bold mx-1.5">
                      Shop Pickup Only
                    </span>
                      )
                    : (
                      <div className="flex items-center mx-2">
                        <Icons
                          name="vehicle"
                          height={16}
                          width={16}
                        />

                        <span className="text-sm text-gray-600 font-bold mx-1.5">
                        <span className="font-normal">
                          {matchedParts != null
                            ? (
                              <>
                                {prefix} {keyword}
                                <strong>
                                  {' '}{suffix}
                                </strong>
                              </>
                              )
                            : (
                                deliveryMsg
                              )}
                        </span>
                      </span>
                      </div>
                      )}
                </div>
              </div>
            </div>
          </div>

        </Link>

        {showStoreTour && (
          <StoreTourMedia media={item?.shopMedia}/>
        )}

      </div>
    </div>
  )
}

export default ShopCard
